import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Loader2, MapPin, X } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * AddressAutocomplete - Google Places Autocomplete Component
 * 
 * Features:
 * - Real-time address suggestions using Google Places API
 * - International address support (US, CA, UK, AU)
 * - Returns full address with lat/lng coordinates
 * - Light theme styling to match iEvntur design
 * 
 * Props:
 * - value: Current input string
 * - onChange: Callback when text changes
 * - onSelect: Callback when place is selected — payload includes:
 *     { name, address, street_number, street, city, state, state_code,
 *       postal_code, country, country_code, lat, lng, place_id }
 * - placeholder: Input placeholder text
 * - country: Restrict to country code (e.g., 'US', 'CA')
 * - searchType: 'address' (default) | 'establishment' | 'geocode'
 *     • address       — street-level autocomplete (e.g. "625 Smith Ave")
 *     • establishment — venues / landmarks (e.g. "Nashville Fairgrounds")
 * - className: Additional CSS classes
 * - disabled: Disable the input
 * - required: Mark as required
 * - error: Error message to display
 */
const AddressAutocomplete = ({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Start typing an address...',
  country = null,
  searchType = 'address',
  className = '',
  disabled = false,
  required = false,
  error = null,
  onApiUnavailable = null,
  'data-testid': testId = 'address-autocomplete'
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  // Iter-213+ P0 — Track Places API runtime errors (quota / denied / etc.)
  // separately from script-load errors so we can surface a "type manually"
  // banner the moment the API stops responding usefully.
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const failureStreak = useRef(0);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimeout = useRef(null);

  // Load Google Maps script. NOTE — when the script is loaded with
  // `loading=async`, the Places library is fetched LAZILY: the script's
  // `onload` fires before `window.google.maps.places` is actually
  // populated. Relying on `onload` alone meant our second effect ran
  // once with `places` still undefined and never re-ran, so the
  // AutocompleteService was never constructed and the dropdown was
  // silently broken. Switch to `google.maps.importLibrary('places')`
  // which is the supported async-ready way to wait for the library.
  useEffect(() => {
    let cancelled = false;

    const markReady = () => {
      if (!cancelled) setScriptLoaded(true);
    };

    const waitForPlaces = async () => {
      try {
        if (window.google?.maps?.importLibrary) {
          await window.google.maps.importLibrary('places');
          markReady();
          return;
        }
        if (window.google?.maps?.places?.AutocompleteService) {
          markReady();
          return;
        }
        // Last-resort polling — covers older script paths or pages where
        // the script was injected by another component just moments ago.
        const t0 = Date.now();
        const poll = () => {
          if (cancelled) return;
          if (window.google?.maps?.places?.AutocompleteService) {
            markReady();
            return;
          }
          if (Date.now() - t0 > 10000) {
            console.warn('Google Places library never finished loading');
            return;
          }
          setTimeout(poll, 150);
        };
        poll();
      } catch (e) {
        console.error('importLibrary(places) failed', e);
        if (!cancelled) setScriptError(true);
      }
    };

    if (window.google?.maps?.places?.AutocompleteService) {
      markReady();
      return () => { cancelled = true; };
    }

    if (!GOOGLE_MAPS_API_KEY) {
      console.warn('Google Maps API key not configured');
      return () => { cancelled = true; };
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      // The script tag exists. Even if its onload already fired,
      // `places` may still be loading. Wait via importLibrary/polling.
      waitForPlaces();
      existingScript.addEventListener('error', () => {
        if (!cancelled) setScriptError(true);
      });
      return () => { cancelled = true; };
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // `onload` fires before Places is necessarily ready. Wait for it.
      waitForPlaces();
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps script');
      if (!cancelled) setScriptError(true);
    };
    document.head.appendChild(script);

    // Iter-176 — surface auth errors from Google (e.g. invalid key /
    // unauthorized referrer) so users see a clear "manual entry" fallback
    // banner instead of a silent failure.
    window.gm_authFailure = () => {
      console.error('Google Maps authentication failed (gm_authFailure)');
      window.__iev_gmaps_auth_failed = true;
      if (!cancelled) setScriptError(true);
    };

    return () => {
      cancelled = true;
      // Don't remove script as it may be used by other components
    };
  }, []);

  // Initialize services when script loads
  useEffect(() => {
    if (scriptLoaded && window.google?.maps?.places?.AutocompleteService) {
      try {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        // Create a dummy div for PlacesService (required by API)
        const dummyDiv = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
      } catch (e) {
        console.error('Failed to construct Places services:', e);
        setScriptError(true);
      }
    }
  }, [scriptLoaded]);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions
  const fetchSuggestions = (input) => {
    if (!autocompleteService.current || input.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    const request = {
      input,
      // Iter-133k — venue/landmark lookup uses 'establishment'; street-level
      // stays 'address'. Google rejects unknown values so we guard here.
      types: searchType === 'establishment'
        ? ['establishment']
        : searchType === 'geocode'
          ? ['geocode']
          : ['address'],
    };

    // Add country restriction if specified
    if (country) {
      request.componentRestrictions = { country };
    }

    autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
      setLoading(false);

      const PSS = window.google.maps.places.PlacesServiceStatus;
      if (status === PSS.OK && predictions) {
        failureStreak.current = 0;
        setApiUnavailable(false);
        setSuggestions(predictions);
        setShowDropdown(true);
        return;
      }

      // ZERO_RESULTS isn't a failure — user just typed something we don't recognise.
      if (status === PSS.ZERO_RESULTS) {
        setSuggestions([]);
        return;
      }

      // Anything else (OVER_QUERY_LIMIT, REQUEST_DENIED, INVALID_REQUEST, UNKNOWN_ERROR)
      // is a real Places API failure. Track a short streak so a single
      // hiccup doesn't trigger the fallback, but two failures in a row do.
      console.warn('Google Places predictions failed:', status);
      failureStreak.current += 1;
      if (failureStreak.current >= 2) {
        setApiUnavailable(true);
        try { onApiUnavailable?.(status); } catch (_) { /* ignore */ }
      }
      setSuggestions([]);
    });
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);
    
    if (onChange) {
      onChange(newValue);
    }

    // Debounce API calls
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (prediction) => {
    if (!placesService.current) return;

    setLoading(true);
    setShowDropdown(false);

    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry', 'address_components', 'name']
      },
      (place, status) => {
        setLoading(false);

        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const components = parseAddressComponents(place.address_components || []);
          
          const fullAddress = place.formatted_address;
          setInputValue(fullAddress);
          
          if (onChange) {
            onChange(fullAddress);
          }
          
          if (onSelect) {
            onSelect({
              name: place.name || null,   // Iter-133k — venue/landmark name from Places
              address: fullAddress,
              street_number: components.street_number,
              street: components.street,
              city: components.city,
              state: components.state,
              state_code: components.state_code,
              postal_code: components.postal_code,
              country: components.country,
              country_code: components.country_code,
              lat: place.geometry?.location?.lat(),
              lng: place.geometry?.location?.lng(),
              place_id: prediction.place_id
            });
          }
        }
      }
    );
  };

  // Parse Google's address components
  const parseAddressComponents = (components) => {
    const result = {
      street_number: '',
      street: '',
      city: '',
      state: '',
      state_code: '',
      postal_code: '',
      country: '',
      country_code: ''
    };

    components.forEach(comp => {
      const types = comp.types;
      
      if (types.includes('street_number')) {
        result.street_number = comp.long_name;
      } else if (types.includes('route')) {
        result.street = comp.long_name;
      } else if (types.includes('locality')) {
        result.city = comp.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        result.state = comp.long_name;
        result.state_code = comp.short_name;
      } else if (types.includes('country')) {
        result.country = comp.long_name;
        result.country_code = comp.short_name;
      } else if (types.includes('postal_code')) {
        result.postal_code = comp.long_name;
      }
    });

    return result;
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  // Clear input
  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    if (onChange) onChange('');
    if (onSelect) onSelect(null);
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`pl-9 pr-8 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 ${error ? 'border-red-500' : ''}`}
          data-testid={testId}
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
        {inputValue && !loading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            tabIndex={-1}
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${
                index === selectedIndex 
                  ? 'bg-cyan-50 text-cyan-900' 
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
              data-testid={`${testId}-suggestion-${index}`}
            >
              <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${index === selectedIndex ? 'text-cyan-500' : 'text-gray-400'}`} />
              <div>
                <p className="text-sm font-medium">
                  {suggestion.structured_formatting?.main_text || suggestion.description}
                </p>
                {suggestion.structured_formatting?.secondary_text && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {suggestion.structured_formatting.secondary_text}
                  </p>
                )}
              </div>
            </button>
          ))}
          <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
            <p className="text-[10px] text-gray-400 flex items-center gap-1">
              <img 
                src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png" 
                alt="Powered by Google" 
                className="h-3"
              />
            </p>
          </div>
        </div>
      )}

      {/* No API key warning */}
      {!GOOGLE_MAPS_API_KEY && (
        <p className="text-xs text-amber-600 mt-1">
          Google Maps API not configured — autocomplete disabled. You can still type the address manually.
        </p>
      )}

      {/* Iter-176 — script failed to load (network / blocked / auth). */}
      {GOOGLE_MAPS_API_KEY && scriptError && (
        <p className="text-xs text-amber-600 mt-1" data-testid={`${testId}-fallback`}>
          Address autocomplete unavailable — type the address manually and we'll save it as entered.
        </p>
      )}

      {/* Iter-213+ P0 — Places API responded with a real error (quota /
          denied / etc.) twice in a row. Surface a CLEAR, actionable
          fallback so users on a flaky network or quota-exhausted key
          aren't trapped. */}
      {GOOGLE_MAPS_API_KEY && !scriptError && apiUnavailable && (
        <p
          className="text-xs text-amber-600 mt-1"
          data-testid={`${testId}-runtime-fallback`}
        >
          Address suggestions are temporarily unavailable.
          Type your full address as-is — we'll verify it on the next step.
        </p>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
